import { memo, useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import Image from 'components/Common/Image'
import SolidBedIcon from 'LuxKit/Icons/solid/SolidBedIcon'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import noop from 'lib/function/noop'
import type { ExploreOffer } from 'tripPlanner/api/explore/types'
import type { TripItineraryData } from 'tripPlanner/api/types'
import AddHotelToTripButton from 'tripPlanner/components/AddToTrip/AddHotelToTripButton'
import AddToTripAction from 'tripPlanner/components/GenericDrawer/AddToTripAction'
import type { LinkRect } from './types'
import { calculatePreviewPosition, PREVIEW_WIDTH, VIEWPORT_PADDING } from './calculatePreviewPosition'

const PreviewContainer = styled.div`
  position: fixed;
  width: ${rem(PREVIEW_WIDTH)};
  max-width: calc(100vw - ${rem(VIEWPORT_PADDING * 2)});
  background-color: ${props => props.theme.palette.neutral.default.eight};
  border-radius: ${props => props.theme.borderRadius.L};
  box-shadow: ${props => props.theme.shadow.flat.large};
  z-index: 5;
  overflow: hidden;
  cursor: pointer;
  animation: fadeInScale 0.15s ease-out;

  @keyframes fadeInScale {
    from {
      opacity: 0;
      transform: scale(0.95) translateY(${rem(4)});
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }
`

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: ${rem(180)};
  flex-shrink: 0;
  overflow: hidden;
`

const ImagePlaceholder = styled.div`
  width: 100%;
  height: 100%;
  background-color: ${props => props.theme.palette.neutral.default.six};
  display: flex;
  align-items: center;
  justify-content: center;
`

const ESTIMATED_PREVIEW_HEIGHT = 300

const AddChipContainer = styled.div`
  position: absolute;
  top: ${rem(12)};
  right: ${rem(12)};
  z-index: 10;
`

interface Props {
  offer: ExploreOffer
  linkRect: LinkRect
  onClick?: () => void
  onClose?: () => void
  onMouseEnter?: () => void
  itineraryData?: TripItineraryData
  onSendMessage?: (message: string) => void
}

function ExploreOfferHoverPreview({
  offer,
  linkRect,
  onClick = noop,
  onClose = noop,
  onMouseEnter: onMouseEnterProp = noop,
  itineraryData,
  onSendMessage,
}: Props) {
  const isExperienceOffer = offer.offerType === 'experience'
  const stopPropagation = useCallback((e: React.MouseEvent) => { e.stopPropagation() }, [])
  const position = useMemo(() => calculatePreviewPosition(linkRect, ESTIMATED_PREVIEW_HEIGHT), [linkRect])

  const positionStyle = useMemo<React.CSSProperties>(() => ({
    left: position.left,
    ...(position.top !== undefined && { top: position.top }),
    ...(position.bottom !== undefined && { bottom: position.bottom }),
  }), [position])

  return (
    <PreviewContainer
      style={positionStyle}
      onMouseEnter={onMouseEnterProp}
      onMouseLeave={onClose}
      onClick={onClick}
    >
      <ImageContainer>
        {offer.imageIds[0] && (
          <Image
            id={offer.imageIds[0]}
            alt={offer.name}
            width={322}
            height={180}
            gravity="auto"
            fit="center"
            loading="eager"
          />
        )}
        {!offer.imageIds[0] && (
          <ImagePlaceholder>
            <SolidBedIcon size="L" colour="neutral-three" />
          </ImagePlaceholder>
        )}
        {onSendMessage && isExperienceOffer && itineraryData && (
          <AddChipContainer onClick={stopPropagation}>
            <AddToTripAction
              itineraryData={itineraryData}
              onSendMessage={onSendMessage}
              placeName={offer.name}
              offerId={offer.id}
              offerType={offer.offerType}
              coordinates={offer.coordinates}
              variant="filled"
              onAfterAction={onClose}
            />
          </AddChipContainer>
        )}
        {onSendMessage && !isExperienceOffer && (
          <AddChipContainer>
            <AddHotelToTripButton
              itineraryData={itineraryData}
              hotelName={offer.name}
              offerId={offer.id}
              offerType={offer.offerType}
              hotelCity={offer.location}
              hotelCoordinates={offer.coordinates}
              onSendMessage={onSendMessage}
              size="medium"
              onAfterAction={onClose}
            />
          </AddChipContainer>
        )}
      </ImageContainer>

      <Sheet padding={16}>
        <Group direction="vertical" gap={4}>
          <Heading variant="heading6">{offer.name}</Heading>
          {offer.description && (
            <BodyText variant="small" colour="neutral-two" lineClamp={2}>
              {offer.description}
            </BodyText>
          )}
          {!offer.description && offer.location && (
            <Group direction="horizontal" gap={4} verticalAlign="center">
              <LineMapMarkerIcon size="XS" colour="neutral-three" />
              <BodyText variant="small" colour="neutral-three">
                {offer.location}
              </BodyText>
            </Group>
          )}
        </Group>
      </Sheet>
    </PreviewContainer>
  )
}

export default memo(ExploreOfferHoverPreview)
